import { z } from 'zod';
import { LIMITS } from './limits.js';
/** Bump when the persisted dataset shape changes incompatibly. */
export const DATASET_SCHEMA_VERSION = 1;
/** Column types are deliberately few. Anything richer belongs to the agent that
 *  prepares the data, not to this app. */
export const ColumnTypeSchema = z.enum(['string', 'number', 'integer', 'date', 'boolean']);
/** Column ids are the stable binding target for widgets. Labels may be renamed
 *  freely; ids may not, because renaming one would silently rebind a widget. */
const ColumnIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'Column ids must be snake_case and start with a letter.');
export const DatasetColumnSchema = z.object({
    id: ColumnIdSchema,
    label: z.string().min(1).max(LIMITS.maxLabelChars),
    type: ColumnTypeSchema,
    /** When false, a null/undefined value in this column is a validation error. */
    nullable: z.boolean().default(true),
    /** Free-text unit, e.g. "hours", "USD". Display only; never used in maths. */
    unit: z.string().max(32).optional()
});
export const DatasetSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(LIMITS.maxLabelChars),
    /** Plain-language description of what exactly one row represents. Required,
     *  because a dataset whose grain nobody can state is a dataset nobody can
     *  aggregate correctly. */
    grain: z.string().min(1).max(500),
    columns: z.array(DatasetColumnSchema).min(1).max(LIMITS.maxDatasetColumns),
    /** Optional declared unique key: a set of column ids whose combination must
     *  be unique across rows. Enforced on every snapshot. */
    uniqueKey: z.array(ColumnIdSchema).max(8).optional(),
    /** Where the supplier says this came from. A provenance *claim*, not a fact
     *  this system verified. */
    sourceDescription: z.string().max(1000).optional(),
    schemaVersion: z.literal(DATASET_SCHEMA_VERSION).default(DATASET_SCHEMA_VERSION),
    createdAt: z.string()
});
/** The parts of a dataset a caller supplies at creation time. */
export const DatasetDraftSchema = DatasetSchema.omit({ id: true, createdAt: true, schemaVersion: true });
/** A single cell. `null` means "known to be absent", and is distinct from a
 *  column being missing from the row entirely. */
export const CellValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const DatasetSnapshotMetaSchema = z.object({
    id: z.string().min(1),
    datasetId: z.string().min(1),
    /** Fingerprint of the dataset schema this snapshot was validated against.
     *  Refusing a mismatched fingerprint is what keeps refresh honest. */
    schemaFingerprint: z.string().min(1),
    schemaVersion: z.literal(DATASET_SCHEMA_VERSION).default(DATASET_SCHEMA_VERSION),
    /** Assigned by the server on receipt. Never supplied by the caller. */
    receivedAt: z.string(),
    /** When the supplier observed the data, if they said. A claim. */
    sourceObservedAt: z.string().optional(),
    /** Supplier's provenance claim for this specific snapshot. */
    sourceDescription: z.string().max(1000).optional(),
    /** Host of the final response the server fetched this snapshot's bytes from.
     *  Observed by the server, never supplied by the caller. */
    fetchedHost: z.string().max(253).optional(),
    /** When the server fetched it. */
    fetchedAt: z.string().optional(),
    rowCount: z.number().int().nonnegative()
});
/** Stable hash of the parts of a dataset schema that widgets bind to.
 *  Labels, units, grain and source text are excluded on purpose: renaming a
 *  column label must not invalidate existing snapshots. */
export function datasetSchemaFingerprint(dataset) {
    const canonical = JSON.stringify({
        columns: [...dataset.columns]
            .map((c) => ({ id: c.id, type: c.type, nullable: c.nullable }))
            .sort((a, b) => a.id.localeCompare(b.id)),
        uniqueKey: dataset.uniqueKey ? [...dataset.uniqueKey].sort() : null
    });
    // FNV-1a 64-bit. Not cryptographic — this only needs to be stable and cheap,
    // and it is never used as a security boundary.
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    const mask = 0xffffffffffffffffn;
    for (let i = 0; i < canonical.length; i++) {
        hash = ((hash ^ BigInt(canonical.charCodeAt(i))) * prime) & mask;
    }
    return `fp1_${hash.toString(36)}`;
}
export function findColumn(dataset, columnId) {
    return dataset.columns.find((c) => c.id === columnId);
}
/** Column types that the numeric aggregations accept. */
export function isNumericColumn(column) {
    return column.type === 'number' || column.type === 'integer';
}
/** "Data as of" — the supplier's observed time when they said one, otherwise
 *  the time this system received the snapshot. Shared by the report header,
 *  the PDF, the report list and the data panel so they never disagree. */
export function dataAsOf(snapshot) {
    return snapshot.sourceObservedAt ?? snapshot.receivedAt;
}
//# sourceMappingURL=dataset.js.map